import { Icons } from '@/components/Icons'
import type { TechKey } from '@/components/HeroCoverage'
import type { ActivationTier } from '@/data/pricing'

// Card "Attivazione e durata del contratto" condivisa tra OfferteCasa e
// OfferteBusiness: intro comune + spiegazione del contributo per la
// tecnologia/rete attiva. Il fornitore della copertura sceglie SOLO la
// narrazione qui sotto (rete propria Spadhausen vs wholesale): il tier
// numerico (`tier`, da activationTier()) per FTTH è sempre lo stesso,
// 36 mesi/costo pieno più alto, indipendentemente dalla rete.
// Tono dei testi: regole editoriali in docs/brand/media-kit/editoriale.md §1.

type Segment = 'casa' | 'business'

interface TechDetail { title: string; body: string }

function detailFor(techKey: TechKey, fornitore: string | undefined, biz: boolean): TechDetail {
  const suSpadhausen = /spadhausen/i.test(fornitore ?? '')

  if (techKey === 'fttc') {
    return {
      title: 'FTTC – Fibra mista rame',
      body: `La FTTC porta la fibra fino all'armadio stradale più vicino ${biz ? 'alla tua sede' : 'a casa tua'}. Da lì arriva${biz ? ' ai tuoi locali' : ''} il doppino in rame che c'è già: niente scavi, niente nuova fibra. Il lavoro tecnico si limita a verificare la linea, attivarla all'armadio e configurare il router. Per questo è il contributo più contenuto tra le nostre tecnologie. Ma non è zero: copre l'attivazione della linea presso il fornitore della rete e il modem che ti consegniamo.`,
    }
  }

  if (techKey === 'ftth') {
    // Senza copertura verificata mostriamo di default la narrazione wholesale; i
    // numeri nei promo-fact restano comunque il tier pieno (36 mesi), uguale per
    // rete propria e wholesale — vedi activationTier().
    if (suSpadhausen) {
      return {
        title: 'FTTH Spadhausen',
        body: `La nostra rete FTTH l'abbiamo costruita noi: cavidotti, dorsali, centrali, accordi con il Comune per usare la pubblica illuminazione. Sono investimenti importanti. Per attivar${biz ? 'e la tua sede' : 'ti a casa'} serve un sopralluogo tecnico, lo scavo dell'ultimo tratto e il rilegamento della fibra fino alla borchia ottica — la presa che installiamo da te. Questi costi li coprono in parte i canoni mensili e in parte il contributo di attivazione: lo riduciamo se scegli di restare con noi il tempo necessario a coprirli.`,
      }
    }
    return {
      title: fornitore ? `FTTH su rete ${fornitore}` : 'FTTH su rete wholesale (OpenFiber, FiberCop, Fastweb)',
      body: `Dove la nostra fibra non arriva, ti portiamo in FTTH appoggiandoci alle reti wholesale — all'ingrosso — di OpenFiber e FiberCop. È sempre fibra fino dentro ${biz ? 'la sede' : 'casa'}, con le stesse prestazioni della nostra. Serve comunque un sopralluogo, a volte la tiratura del cavo dalla borchia ottica al punto dove va il router, e la configurazione dell'apparato. Il contributo di attivazione copre il lavoro tecnico, l'attivazione della linea presso il fornitore e il modem Wi-Fi 6 che ti consegniamo.`,
    }
  }

  // FWA: senza copertura raccontiamo la rete proprietaria (lo scenario tipico,
  // coerente con la descrizione di listino); il tier non cambia tra le due reti.
  if (fornitore && !suSpadhausen) {
    return {
      title: `FWA su rete ${fornitore}`,
      body: `Dove la nostra rete radio non arriva, ti portiamo la connettività FWA appoggiandoci a infrastrutture di altri operatori. Il lavoro tecnico è lo stesso: sopralluogo, montaggio dell'antenna sul tetto, allineamento del segnale, cavo fino al router. Il contributo copre il tempo del tecnico, l'apparato installato e l'attivazione della linea presso il fornitore della rete. Anche qui, se disattivi, l'antenna sul tetto va restituita.`,
    }
  }
  return {
    title: 'FWA su rete proprietaria Spadhausen',
    body: `La nostra rete radio è quella che dal 2008 ci permette di arrivare nelle frazioni e nelle zone dove la fibra non arriverebbe per anni. È fatta di torri (BTS), collegamenti in fibra e apparati radio: l'abbiamo costruita noi, tassello per tassello. Per attivarti, un nostro tecnico verifica con un sopralluogo che il ripetitore più vicino sia visibile dal tuo tetto, monta una piccola antenna, la allinea al segnale e posa il cavo fino al router. Il contributo copre il lavoro in altezza, l'apparato installato ${biz ? 'nella tua sede' : 'a casa tua'} e una quota dell'infrastruttura che ti porta la connessione dove altri operatori non sono mai voluti venire. Se disattivi, l'antenna sul tetto va restituita.`,
  }
}

export function ActivationStrip({ segment, techKey, fornitore, tier }: {
  segment: Segment
  techKey: TechKey
  fornitore?: string
  tier: ActivationTier
}) {
  const biz = segment === 'business'
  const detail = detailFor(techKey, fornitore, biz)
  const unit = biz ? '+ IVA' : ' attivazione'

  return (
    <div className="promo-strip">
      <div className="promo-strip-icn"><Icons.bolt /></div>
      <div className="promo-strip-body">
        <div className="promo-strip-head">Attivazione e durata del contratto</div>
        <h3>Nessun vincolo nascosto. Solo costi sostenibili.</h3>
        <div className="promo-strip-cols">
          <div className="promo-strip-sub">
            <p>
              Spadhausen non ti lega. Ti diamo la possibilità di abbassare i costi in modo sostenibile: noi anticipiamo le spese, tu le recuperi nel tempo. Il contributo di attivazione non è una penale travestita. Copre costi tecnici reali: il sopralluogo, il lavoro del tecnico, gli apparati installati {biz ? 'nella tua sede' : 'a casa tua'}, l'attivazione della linea presso il fornitore quando la rete non è la nostra. E lo riduciamo in cambio di una sola promessa: restare con noi il tempo che serve a coprire quello che anticipiamo per te.
            </p>
            <p>
              Se cambi idea prima, non paghi penali. Paghi solo il costo di gestione previsto dalla delibera AGCOM 487/18/CONS e, se previsto, restituisci gli apparati installati (antenna, ONT, modem). Finito il periodo di recupero, il canone resta bloccato e puoi andartene quando vuoi.
            </p>
            <p>
              Ogni tecnologia ha un contributo diverso, perché diverso è il lavoro tecnico richiesto. Ecco, in trasparenza, cosa paghi e perché.
            </p>
          </div>
          <div className="promo-strip-tech">
            <h4>{detail.title}</h4>
            <p>{detail.body}</p>
          </div>
        </div>
        <div className="promo-facts">
          <div className="promo-fact">
            <span className="pf-label">Con impegno {tier.mesi} mesi</span>
            <span className="pf-value">€ {tier.vincolato}<em>{unit}</em></span>
          </div>
          <div className="promo-fact">
            <span className="pf-label">Senza vincolo</span>
            <span className="pf-value">€ {tier.pieno}<em>{unit}</em></span>
          </div>
          <div className="promo-fact">
            <span className="pf-label">Canone mensile</span>
            <span className="pf-value">sempre bloccato</span>
          </div>
        </div>
      </div>
    </div>
  )
}
